<template>
  <div class="section-card">
    <!-- 头部 -->
    <div class="section-header">
      <div class="header-left">
        <span class="section-title">服务配置</span>
        <span class="status-badge" :class="killPigEnabled ? 'on' : 'off'">
          {{ killPigEnabled ? '运行中' : '已关闭' }}
        </span>
      </div>
      <NSwitch :value="killPigEnabled" @update:value="emit('update:killPigEnabled', $event)" size="large" />
    </div>

    <!-- 新批次保护 + 高级设置 (Ctrl+Shift+D 统一显示/隐藏) -->
    <template v-if="debugMode">
    <div class="tile-row">
      <div class="tile-label">新批次保护</div>
      <NSwitch v-model:value="input.SkipKillInNewGame" size="large" />
    </div>
    <div class="tile-hint">新批次前三轮暂不干预，保持数据均衡</div>
    </template>

    <!-- 运行状态 -->
    <div class="stats-bar">
      <div class="stat-item">
        <span class="stat-dot" :class="killPigEnabled ? 'dot-on' : 'dot-off'"></span>
        <span class="stat-text">{{ killPigEnabled ? '服务运行中' : '服务已暂停' }}</span>
      </div>
      <div class="stat-item">
        <span class="stat-val">{{ selectRooms.length }}</span>
        <span class="stat-unit">/ {{ roomList.length }} 房间</span>
      </div>
    </div>

    <!-- 拉取数据（控制上游 WebSocket 与视频链接拉取/重试，由遥控器保存开关） -->
    <div class="field-card auto-bet-card">
      <div class="field-header">
        <span class="field-icon">D</span>
        <span class="field-label">拉取数据</span>
        <NSwitch v-model:value="input.PullDataEnabled" size="medium" style="margin-left:auto" />
      </div>
      <div class="field-hint">
        {{ input.PullDataEnabled ? '已开启：正常接收上游 WebSocket 数据与视频链接重试' : '已关闭：已断开全部数据连接，等待开启' }}
      </div>
    </div>

    <!-- 保持链接（真实下注，由遥控器保存开关） -->
    <div class="field-card auto-bet-card">
      <div class="field-header">
        <span class="field-icon">B</span>
        <span class="field-label">保持链接</span>
        <NSwitch v-model:value="input.AutoBetEnabled" size="medium" style="margin-left:auto" />
      </div>
      <div class="auto-bet-grid">
        <div class="auto-bet-item">
          <span class="auto-bet-label">间隔(分钟)</span>
          <NInputNumber v-model:value="input.AutoBetIntervalMin" :show-button="false" :min="1" :max="120" size="small" />
        </div>
        <div class="auto-bet-item">
          <span class="auto-bet-label">单注金额</span>
          <NInputNumber v-model:value="input.AutoBetAmount" :show-button="false" :min="0" size="small" />
        </div>
      </div>
      <div class="balance-row">
        <span class="balance-label">账号余额</span>
        <span class="balance-val">{{ realBalance != null ? '¥ ' + realBalance.toFixed(2) : '—' }}</span>
      </div>
    </div>

    <!-- 最低启动金额 -->
    <div class="field-card">
      <div class="field-header">
        <span class="field-icon">$</span>
        <span class="field-label">参与门槛</span>
      </div>
      <NInputNumber v-model:value="input.KillStartAmount" :show-button="false" :min="0" size="large" class="field-input" />
      <div class="field-hint">
        {{ input.KillStartAmount === 0 ? '不限制，所有批次均适用' : '参与金额 ≥ ' + input.KillStartAmount + ' 元才生效' }}
      </div>
    </div>

    <!-- 高级设置 (Ctrl+Shift+D 统一显示/隐藏) -->
    <template v-if="debugMode">
    <NCollapse>
      <NCollapseItem title="高级设置" name="advanced">
        <!-- 运行参数 -->
        <div class="sub-title">运行参数</div>
        <div class="config-grid">
          <div class="config-card">
            <div class="config-card-header">
              <span class="config-icon">H</span>
              <span class="config-label">批次启动保护</span>
            </div>
            <NInputNumber v-model:value="input.HeaderReserveJuNumber" :show-button="false" :max="1000" :min="1" size="large" />
            <span class="config-desc">换主播后前 {{ input.HeaderReserveJuNumber }} 轮暂不干预</span>
          </div>
          <div class="config-card">
            <div class="config-card-header">
              <span class="config-icon">T</span>
              <span class="config-label">批次收尾保护</span>
            </div>
            <NInputNumber v-model:value="input.EndReserveJuNumber" :show-button="false" :max="1000" :min="2" size="large" />
            <span class="config-desc">至少保留 {{ input.EndReserveJuNumber }} 轮，保持数据连续</span>
          </div>
        </div>
        <div class="config-card single">
          <div class="config-card-header">
            <span class="config-icon">P</span>
              <span class="config-label">换主播保护期</span>
          </div>
          <NInputNumber v-model:value="input.DealerSearchStartRange" :show-button="false" :max="1000" :min="0" size="large" />
          <span class="config-desc">
            {{ input.DealerSearchStartRange === 0 ? '不限制，所有批次均适用' : '换主播后前 ' + input.DealerSearchStartRange + ' 轮暂不干预，避免误判' }}
          </span>
        </div>

        <!-- 主播白名单 -->
        <div class="sub-title">主播白名单</div>
        <div class="whitelist-wrap">
          <div class="whitelist-header">
            <span>已添加</span>
            <span class="whitelist-count">{{ blockList.length }} 人</span>
          </div>
          <div class="whitelist-body" v-if="blockList.length">
            <div class="whitelist-row" v-for="v in blockList" :key="v.id">
              <span class="whitelist-name">{{ v.dealer_name }}</span>
              <n-popconfirm @positive-click="emit('deleteDealer', v.id!)">
                <template #trigger>
                  <span class="whitelist-del">删除</span>
                </template>
                确定删除 {{ v.dealer_name }} 吗？
              </n-popconfirm>
            </div>
          </div>
          <div class="whitelist-empty" v-else>暂无白名单</div>
          <NButton size="small" quaternary @click="emit('addDealer')" class="add-btn">+ 添加主播</NButton>
        </div>

        <!-- 数据管理 -->
        <div class="sub-title">数据管理</div>
        <div class="data-mgmt">
          <div class="data-mgmt-item">
            <div class="data-mgmt-info">
              <span class="data-mgmt-label">清空交易数据</span>
              <span class="data-mgmt-desc">清空后只统计此时间点之后的交易记录</span>
            </div>
            <NButton size="small" type="warning" @click="emit('clearBetCache')" ghost>清空交易</NButton>
          </div>
          <div class="data-mgmt-item">
            <div class="data-mgmt-info">
              <span class="data-mgmt-label">清空账号显示</span>
              <span class="data-mgmt-desc">清空后只显示此时间点后有交易记录的账号</span>
            </div>
            <NButton size="small" type="warning" @click="emit('clearAccountCache')" ghost>清空账号</NButton>
          </div>
        </div>
      </NCollapseItem>
    </NCollapse>
    </template>

    <!-- 保存 -->
    <NButton type="primary" size="large" @click="emit('save')" block>保存设置</NButton>
  </div>
</template>

<script setup lang="ts">
import RoomVO from '@/ts/VO/RoomVO';
import type virtual_blacklist from '@/ts/dbvo/virtual_blacklist';
import type KillPigConfigVO from '@/ts/VO/KillPigConfigVO';
import { NCollapse, NCollapseItem, NInputNumber, NSwitch, NButton, NPopconfirm } from 'naive-ui';
import { inject, ref, type Ref } from 'vue';

const props = defineProps<{
  input: KillPigConfigVO;
  killPigEnabled: boolean;
  realBalance: number;
  selectRooms: (string | number)[];
  roomList: RoomVO[];
  blockList: virtual_blacklist[];
}>();

const emit = defineEmits<{
  'update:killPigEnabled': [value: boolean];
  save: [];
  addDealer: [];
  deleteDealer: [id: number];
  clearBetCache: [];
  clearAccountCache: [];
}>();

// 调试模式统一控制所有快捷显示
const debugMode = inject<Ref<boolean>>('debugMode', ref(false));
</script>

<style scoped lang="scss">
$bg: #2a2a2e;
$card: #323236;
$hover: #3a3a40;
$muted: #6b6b76;
$accent: #5b8def;
$warn: #e8a040;
$danger: #e05555;
$success: #4caf88;
$radius: 10px;

.section-card {
  background: $bg;
  border-radius: 14px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 18px;
  flex: 1;
  min-height: 0;
  overflow-y: auto;

  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb { background: rgba(255,255,255,.12); border-radius: 2px; }
}

// ---------- header ----------
.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;

  .header-left {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .section-title {
    font-size: 17px;
    font-weight: 700;
    color: #e8e8ed;
  }

  .status-badge {
    font-size: 11px;
    font-weight: 600;
    padding: 3px 10px;
    border-radius: 12px;
    letter-spacing: .3px;

    &.on {
      background: rgba($success, .15);
      color: $success;
    }
    &.off {
      background: rgba($muted, .15);
      color: $muted;
    }
  }
}

// ---------- tile row ----------
.tile-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: $card;
  border-radius: $radius;
  padding: 14px 16px;
}

.tile-label {
  font-size: 14px;
  font-weight: 600;
  color: #e0e0e6;
}

.tile-hint {
  font-size: 11px;
  color: $muted;
  margin-top: -12px;
  padding-left: 2px;
}

// ---------- stats bar ----------
.stats-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 2px;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: $muted;
}

.stat-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;

  &.dot-on  { background: $success; box-shadow: 0 0 6px rgba($success, .5); }
  &.dot-off { background: $muted; }
}

.stat-val {
  font-weight: 700;
  font-size: 16px;
  color: #d0d0d8;
}

.stat-unit {
  font-size: 11px;
}

// ---------- field card ----------
.field-card {
  background: $card;
  border-radius: $radius;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.field-header {
  display: flex;
  align-items: center;
  gap: 8px;
}

.field-icon {
  width: 22px;
  height: 22px;
  border-radius: 6px;
  background: rgba($accent, .18);
  color: $accent;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
}

.field-label {
  font-size: 13px;
  font-weight: 600;
  color: #c8c8d0;
}

.field-input {
  width: 100%;
}

.field-hint {
  font-size: 11px;
  color: $muted;
  line-height: 1.5;
}

// ---------- 保持链接（紧凑卡片） ----------
.auto-bet-card {
  padding: 12px 14px;
  gap: 8px;
}

.auto-bet-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.auto-bet-item {
  display: flex;
  flex-direction: column;
  gap: 4px;

  :deep(.n-input-number) {
    width: 100%;
  }
}

.auto-bet-label {
  font-size: 11px;
  color: $muted;
}

// ---------- 实时余额 ----------
.balance-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: rgba($success, .10);
  border: 1px solid rgba($success, .22);
  border-radius: 8px;
  padding: 10px 14px;
}

.balance-label {
  font-size: 12px;
  font-weight: 600;
  color: $muted;
}

.balance-val {
  font-size: 18px;
  font-weight: 800;
  color: $success;
  letter-spacing: .3px;
}

// ---------- sub-title ----------
.sub-title {
  font-size: 12px;
  font-weight: 700;
  color: $muted;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 10px;
  margin-top: 18px;
  padding-left: 2px;

  &:first-child { margin-top: 8px; }
}

// ---------- config grid + cards ----------
.config-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-bottom: 10px;
}

.config-card {
  background: $card;
  border-radius: $radius;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;

  &.single {
    grid-column: 1 / -1;
  }

  .config-card-header {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .config-icon {
    width: 20px;
    height: 20px;
    border-radius: 5px;
    background: rgba($accent, .14);
    color: $accent;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    font-weight: 700;
  }

  .config-label {
    font-size: 12px;
    font-weight: 600;
    color: #c8c8d0;
  }

  .config-desc {
    font-size: 11px;
    color: $muted;
    line-height: 1.4;
  }
}

// ---------- whitelist ----------
.whitelist-wrap {
  background: $card;
  border-radius: $radius;
  padding: 14px;
  margin-bottom: 2px;
}

.whitelist-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
  color: $muted;
  margin-bottom: 10px;
}

.whitelist-count {
  font-weight: 600;
  color: #c0c0c8;
}

.whitelist-body {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 10px;
  max-height: 22vh;
  overflow-y: auto;

  &::-webkit-scrollbar { width: 3px; }
  &::-webkit-scrollbar-thumb { background: rgba(255,255,255,.10); border-radius: 2px; }
}

.whitelist-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: rgba(255,255,255,.03);
  border-radius: 6px;
  transition: background .15s;

  &:hover { background: rgba(255,255,255,.06); }
}

.whitelist-name {
  font-size: 13px;
  color: #d4d4db;
}

.whitelist-del {
  font-size: 11px;
  color: $danger;
  cursor: pointer;
  padding: 2px 6px;
  border-radius: 4px;
  transition: background .15s;

  &:hover { background: rgba($danger, .12); }
}

.whitelist-empty {
  font-size: 12px;
  color: $muted;
  padding: 12px 0;
  margin-bottom: 8px;
}

.add-btn {
  font-size: 12px;
}

// ---------- data management ----------
.data-mgmt {
  background: $card;
  border-radius: $radius;
  padding: 4px 0;
  overflow: hidden;
}

.data-mgmt-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 14px;
  transition: background .15s;

  &:not(:last-child) {
    border-bottom: 1px solid rgba(255,255,255,.06);
  }

  &:hover { background: rgba(255,255,255,.02); }
}

.data-mgmt-info {
  display: flex;
  flex-direction: column;
  gap: 3px;
  flex: 1;
  margin-right: 12px;
}

.data-mgmt-label {
  font-size: 13px;
  font-weight: 600;
  color: #d0d0d8;
}

.data-mgmt-desc {
  font-size: 11px;
  color: $muted;
  line-height: 1.4;
}

// ---------- responsive ----------
@media (max-width: 480px) {
  .section-card { padding: 14px; gap: 14px; }
  .config-grid { grid-template-columns: 1fr; }
}
</style>
